<template>
  <v-dialog v-model="visible" max-width="800px" persistent>
    <v-card>
      <!-- Header -->
      <v-toolbar flat color="secondary" dark>
        <v-toolbar-title>
          <v-icon left>mdi-key-outline</v-icon>
          Permisos de {{ user?.firstName || 'Usuario' }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Body -->
      <v-card-text>
        <v-alert type="info" dense text>
          Selecciona los permisos que este usuario puede ejercer.
        </v-alert>

        <v-skeleton-loader v-if="loading" type="list-item" class="mt-4" />

        <v-alert v-else-if="!permissionsGrouped.length" type="warning" text class="mt-4">
          No hay permisos disponibles para mostrar.
        </v-alert>

        <v-treeview
          v-else
          :items="permissionsGrouped"
          activatable
          open-on-click
          multiple
          v-model="selectedPermissions"
          item-key="id"
          :selection-type="'independent'"
          :open.sync="openCategories"
          dense
          transition
        >
          <!-- Categoría -->
          <template v-slot:prepend="{ item }">
            <v-icon v-if="!item.perm" color="primary">mdi-folder-outline</v-icon>
            <v-icon v-else color="primary" small>mdi-shield-check</v-icon>
          </template>

          <!-- Etiqueta -->
          <template v-slot:label="{ item }">
            <div v-if="item.perm" class="d-flex flex-column">
              <span class="font-weight-medium">{{ item.perm.description }}</span>
            </div>
            <div v-else class="font-weight-bold text-uppercase">
              {{ item.name }}
            </div>
          </template>
        </v-treeview>

        <!-- Controles -->
        <div
          v-if="permissionsGrouped.length"
          class="mt-4 d-flex align-center justify-space-between"
        >
          <div class="grey--text">
            {{ selectedPermissions.length }} permisos seleccionados
          </div>
          <div>
            <v-btn text small @click="toggleSelectAll">
              {{ allSelected ? 'Limpiar selección' : 'Seleccionar todos' }}
            </v-btn>
          </div>
        </div>
      </v-card-text>

      <!-- Footer -->
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('close')">Cancelar</v-btn>
        <v-btn color="primary" @click="savePermissions" :loading="saving">
          Guardar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import PermissionService from '../../services/PermissionService'

export default {
  name: 'UserPermissionsDialog',
  props: {
    user: { type: Object, required: true },
    value: { type: Boolean, default: true }
  },
  data: () => ({
    visible: true,
    loading: true,
    saving: false,
    permissionsGrouped: [],
    selectedPermissions: [],
    openCategories: []
  }),
  computed: {
    allSelected () {
      if (!this.permissionsGrouped.length) return false
      const allIds = this.permissionsGrouped.flatMap(c => c.children.map(p => p.id))
      return allIds.every(id => this.selectedPermissions.includes(id))
    }
  },
  watch: {
    value (val) { this.visible = val },
    visible (val) { if (!val) this.$emit('close') }
  },
  mounted () {
    this.loadPermissions()
  },
  methods: {
    async loadPermissions () {
      try {
        this.loading = true
        const { items } = await PermissionService.list()

        // Agrupar permisos por categoría
        const grouped = {}
        items.forEach((perm) => {
          const category = perm.category || 'Otros'
          if (!grouped[category]) grouped[category] = []
          grouped[category].push({ id: perm.id, perm })
        })

        // Transformar en estructura de árbol
        this.permissionsGrouped = Object.keys(grouped).map(category => ({
          id: category,
          name: category.toUpperCase(),
          children: grouped[category]
        }))

        const { items: currentlySelected } = await PermissionService.list({ clientUserId: this.user.userId })
        this.selectedPermissions = currentlySelected
        this.openCategories = Object.keys(grouped)
      } catch (error) {
        console.error('Error al cargar permisos:', error)
        this.$toast?.error('No se pudieron cargar los permisos')
      } finally {
        this.loading = false
      }
    },
    async savePermissions () {
      try {
        this.saving = true
        await this.$axios.put(`/api/users/${this.user.id}/permissions`, {
          permissions: this.selectedPermissions
        })
        this.$toast?.success('Permisos actualizados correctamente')
        this.$emit('close')
      } catch (error) {
        console.error(error)
        this.$toast?.error('Error al guardar permisos')
      } finally {
        this.saving = false
      }
    },
    toggleSelectAll () {
      if (this.allSelected) {
        this.selectedPermissions = []
      } else {
        const allIds = this.permissionsGrouped.flatMap(c => c.children.map(p => p.id))
        this.selectedPermissions = allIds
      }
    }
  }
}
</script>

<style scoped>
.v-treeview-node__label {
  line-height: 1.3;
}
.v-treeview-node__root {
  padding-top: 3px;
  padding-bottom: 3px;
}
</style>
